import { HELLOASSO_FORM_TYPE } from '~/models/enums'
import { isFrenchTimezoneId } from '~/utils/data'
import { request } from '~/utils/request'

export const actions = {
  /**
   * Remove the HelloAsso organization from the club.
   * @param {String|Number} clubId the club identifier.
   * @returns {Promise}
   */
  async deleteOne(_, clubId) {
    await request(this, 'club').delete('/clubs/{clubId}/helloasso', { clubId })
  },

  /**
   * Fetch HelloAsso data of a club.
   * @param {String|Number} clubId the club identifier.
   * @returns {Promise<Object>}
   *
   * @deprecated
   * The data returned by this method is the same as the data loaded with the club in Club.group_hello_asso_data.
   * Maybe this endpoint should be updated to return more data?
   */
  async fetchOneByClub(_, clubId) {
    const { data } = await request(this, 'club').get(
      '/clubs/{clubId}/helloasso',
      { clubId }
    )

    return data
  },

  /**
   * List all payments of an HelloAsso organization.
   * @returns {Promise<Object>}
   */
  async fetchPaymentList(
    _,
    { clubId, filters, nextPageToken, perPage, sortOrder }
  ) {
    const { data } = await request(this, 'club').get(
      '/clubs/{clubId}/helloasso/payments',
      { clubId },
      {
        ...filters,
        next_page_token: nextPageToken,
        per_page: perPage,
        sort: sortOrder,
      }
    )

    return data
  },

  /**
   * List HelloAsso forms of a club.
   * @returns {Promise<Object>}
   */
  async fetchFormList(_, { clubId, filters, nextPageToken, perPage }) {
    const { data } = await request(this, 'club').get(
      '/clubs/{clubId}/helloasso/forms',
      { clubId },
      {
        ...filters,
        next_page_token: nextPageToken,
        per_page: perPage,
      }
    )

    /**
     * FIXME
     * This item seems to be a default form, and is not visible in HelloAsso dashboard.
     * It will confuse our users, so ignore it in the results if set.
     */
    const checkoutIndex = data.data?.findIndex(
      (item) =>
        item.formSlug === 'default' &&
        item.formType === HELLOASSO_FORM_TYPE.CHECKOUT
    )
    if (checkoutIndex > -1) {
      data.data.splice(checkoutIndex, 1)
    }

    return data
  },

  /**
   * Send a payment notification for an HelloAsso form to given players.
   * @param {String|Number} clubId the club identifier.
   * @param {String} formType the form type.
   * @param {String} formSlug the f
   * orm identifier.
   * @param {Array<String|Number>} playersIds a list of player identifiers.
   * @returns {Promise<Object>}
   */
  async sendHelloAssoFormToPlayers(
    _,
    { clubId, formType, formSlug, playersIds, paymentInstructions }
  ) {
    const { data } = await request(this, 'club').post(
      '/clubs/{clubId}/helloasso/forms/{formType}/{formSlug}/notify-players',
      {
        clubId,
        formType,
        formSlug,
      },
      {
        players_ids: playersIds,
        payment_instructions: paymentInstructions,
      }
    )

    return data
  },
}

export const getters = {
  /**
   * Get the HelloAsso data of a club.
   * @param {String|Number} clubId the club identifier.
   * @returns {Object}
   */
  getByClub: (state, getters, rootState, rootGetters) => (clubId) => {
    const club = rootGetters['clubs/get'](clubId)
    return club?.group_hello_asso_data
  },

  /**
   * Whether if HelloAsso behaviors can be used by the current player for the current club.
   * @param {Object} state the current state.
   * @param {Object} getters the getters of the current store.
   * @param {Object} rootState all the states.
   * @param {Object} rootGetters all the getters available.
   * @returns {Boolean}
   */
  isHelloAssoAvailable: (state, getters, rootState, rootGetters) => {
    const club = rootGetters['clubs/getSelected']
    return isFrenchTimezoneId(club?.timezone_id)
  },
  /**
   * Whether if HelloAsso behaviors can be used by the current player for a given club.
   * @param {Object} state the current state.
   * @param {Object} getters the getters of the current store.
   * @param {Object} rootState all the states.
   * @param {Object} rootGetters all the getters available.
   * @param {String|Number} clubId the club identifier.
   * @returns {Boolean}
   */
  isHelloAssoAvailableForClub:
    (state, getters, rootState, rootGetters) => (clubOrClubId) => {
      const club =
        typeof clubOrClubId === 'object'
          ? clubOrClubId
          : rootGetters['clubs/get'](clubOrClubId)
      return isFrenchTimezoneId(club?.timezone_id)
    },
}
